import { DateTime } from "luxon";
import { environment } from "src/environments/environment";
import { BehaviorSubject, Subject } from "rxjs";
import { TranslateService } from "../../services/translate.service";

import * as helpers from '../../helpers/ui-helpers';
import { AppData } from "src/app/data";
import { RouteProviderService } from "../../services/route-provider.service";
import { DispatcherService } from "../../services/dispatcher.service";
import { AlertController, ModalController } from "@ionic/angular";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { Location } from 'src/app/types/location.type';
import { LocationService } from "../../services/location.service";
import { PassService } from "../../services/pass.service";
import { NotificationService } from "../../services/notification.service";

export const RouteEventActionColors = [ 'primary', 'secondary', 'danger', 'medium', 'clear' ] as const;
export const RouteEventActionColorMap: Record<RouteEventActionColor, RouteEventActionColor> = {
  primary: 'primary', secondary: 'secondary', danger: 'danger', medium: 'medium', clear: 'clear',
};
export type RouteEventActionColor = typeof RouteEventActionColors[number]

export const RouteEventActionFill = [ 'outline', 'solid' ] as const;
export type RouteEventActionFill = typeof RouteEventActionFill[number]
export const RouteEventActionFillMap: Record<RouteEventActionFill, RouteEventActionFill> = {
  outline: 'outline', solid: 'solid',
};

export const RouteEventActionTypes = [ "onTheWay", "arrive", "perform", "depart", "navigate", "notify", "seeOn", "generic" ] as const;
export type RouteEventActionType = typeof RouteEventActionTypes[number];
export const RouteEventActionTypeMap: Record<RouteEventActionType, RouteEventActionType> = {
  onTheWay: "onTheWay", arrive: "arrive", perform: "perform", depart: "depart", navigate: "navigate", notify: "notify", seeOn: "seeOn", generic: "generic",
};

export enum GroupActionSortByType {
  PullOut,
  Waypoint,
  BreakEnd,
  OutOfServicePeriodEnd,
  Dropoff,
  ScheduledFlagStop,
  Pickup,
  BreakStart,
  OutOfServicePeriodStart,
  PullIn,
}

export type RouteEventAction = {
  type: RouteEventActionType
  label: string,
  icon: string,
  color: RouteEventActionColor,
  fill: RouteEventActionFill,
  preview: boolean,
  drivingMode: boolean,
  disabled: boolean,
  fn: (action: RouteEventAction) => void | Promise<void>
}
export type HeaderInfo = {
  time?: string,
  type: string,
  subtitle?: string,
  timeTo?: string,
  /** example rider name */
  details?: string,
}

export type BuildServices = {
  alertController: AlertController
  appData: AppData
  browserTitle: Title
  dispatchSvc: DispatcherService
  locationSvc: LocationService
  modalController: ModalController
  notificationSvc: NotificationService
  passSvc: PassService
  routeProviderSvc: RouteProviderService
  router: Router
  translateSvc: TranslateService
}

export class GenericRouteEvent {

  public readonly trackBy: string;
  protected alertController: AlertController;
  protected appData: AppData;
  protected browserTitle: Title;
  protected dispatchSvc: DispatcherService;
  protected locationSvc: LocationService;
  protected modalController: ModalController;
  protected notificationSvc: NotificationService;
  protected passSvc: PassService;
  protected routeProviderSvc: RouteProviderService;
  protected router: Router;
  protected translateSvc: TranslateService;

  protected destroyed$ = new Subject<boolean>();
  public id: string | number;
  public headerInfo: HeaderInfo = { type: '' };
  public enabled: boolean = !environment.features.enforceScheduledBreakTimes;

  public started: boolean = false;
  public scheduledStart: DateTime | null = null;
  public scheduledEnd: DateTime | null = null;
  public departedTime: DateTime | null = null;
  public timer: string = '';

  public type = 'generic';
  public value: any;

  protected readonly _features = environment.features;
  private readonly _completed$ = new BehaviorSubject<boolean>(false);
  public readonly completed$ = this._completed$.asObservable();
  private _completed: boolean = false;
  public get completed() { return this._completed; }
  public set completed(value) {
    this._completed$.next(value);
    this._completed = value;
  }

  public location: Location | null = null;
  public phoneNumber: string = '';
  public stopSequence: number = 0;
  public groupStopOrder = Infinity;

  // UI Properties
  public disabled: boolean = environment.features.onlyAtNextLocationEnabled;
  public isLate: boolean = false;
  public tapForDetails: () => Promise<void> | null = null;
  public actions: RouteEventAction[] = [];
  public typeIcons: helpers.Icon[] = [];
  public detailIcons: helpers.Icon[] = [];
  public detailBadges: helpers.Badge[] = [];

  public updateTimeBasedProperties = () => {};
  public onTheWay = async (action?: RouteEventAction) => { };
  public arrive = async (event: { action?: RouteEventAction, checkLocation?: boolean, checkOdometer?: boolean }) => { };
  public depart = async (event: { action?: RouteEventAction, checkLocation?: boolean, checkOdometer?: boolean }) => { };
  public notify = async (action?: RouteEventAction, displayNotifications?: boolean) => { };

  constructor(services: BuildServices) {
    this.alertController = services.alertController;
    this.appData = services.appData;
    this.browserTitle = services.browserTitle;
    this.dispatchSvc = services.dispatchSvc;
    this.locationSvc = services.locationSvc;
    this.modalController = services.modalController;
    this.notificationSvc = services.notificationSvc;
    this.passSvc = services.passSvc;
    this.routeProviderSvc = services.routeProviderSvc;
    this.router = services.router;
    this.translateSvc = services.translateSvc;

    this.trackBy = this._generateGuid();
  }

  public getActions(): RouteEventAction[] { return []; }

  /** Kill listeners & subscriptions */
  public destroy(): void{
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private _generateGuid(): string {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
      s4() + '-' + s4() + s4() + s4();
  }
}
